import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CustomizationDataDto,
  KSendSuccessResponse,
  TableColumnConfigDto,
} from '@asset-metrix/portal-gateway-client-js';
import { WidgetProps } from '@asset-metrix/portal-ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  getTableCustomizationMutationScopeId,
  mutateTableCustomization,
} from '../../api/portalGateway/tableCustomization';

import { convertToCustomizationData } from './convertToCustomizationData';
import { patchColumnsForGroupOnlyMode } from './patchColumnsForGroupOnlyMode';
import { useGroupOnlyVisualization } from './useGroupOnlyVisualization';

type GroupOnlyCustomizationMutationVariables = {
  widgetId: string;
  data: CustomizationDataDto;
  persistGeneration: number;
  isStaleCleanup?: boolean;
};

type GroupOnlyCustomizationMutationContext = {
  previous: unknown;
};

export type UseGroupOnlyCustomizationProps = {
  customizationData: CustomizationDataDto | null | undefined;
  customizedColumns: TableColumnConfigDto[];
  enableGroupOnlyVisualization?: boolean;
  isCustomizable: boolean;
  widgetId: string;
};

type UseGroupOnlyCustomizationResult = {
  customizedColumns: TableColumnConfigDto[];
  groupOnlyVisualizationData: WidgetProps['chipGroup'];
};

export function useGroupOnlyCustomization({
  customizationData,
  customizedColumns: rawCustomizedColumns,
  enableGroupOnlyVisualization,
  isCustomizable,
  widgetId,
}: UseGroupOnlyCustomizationProps): UseGroupOnlyCustomizationResult {
  const queryClient = useQueryClient();
  const [localGroupOnlyColumnId, setLocalGroupOnlyColumnId] = useState<
    string | null
  >(null);

  const clearedStaleGroupOnlyColumnIdRef = useRef<string | null>(null);
  const groupOnlyPersistGenerationRef = useRef(0);

  useEffect(() => {
    clearedStaleGroupOnlyColumnIdRef.current = null;
    groupOnlyPersistGenerationRef.current = 0;
  }, [widgetId]);

  const {
    mutate: mutateGroupOnlyColumnId,
    isPending: isGroupOnlyColumnIdPending,
  } = useMutation<
    KSendSuccessResponse,
    Error,
    GroupOnlyCustomizationMutationVariables,
    GroupOnlyCustomizationMutationContext
  >({
    mutationFn: mutateTableCustomization,
    scope: {
      id: getTableCustomizationMutationScopeId(widgetId),
    },
    onMutate: async ({ widgetId: mutationWidgetId, data }) => {
      await queryClient.cancelQueries({
        queryKey: ['customization', { widgetId: mutationWidgetId }],
      });
      const previous = queryClient.getQueryData([
        'customization',
        { widgetId: mutationWidgetId },
      ]);
      queryClient.setQueryData(
        ['customization', { widgetId: mutationWidgetId }],
        { data }
      );

      return { previous };
    },
    onError: (_error, variables, context) => {
      if (context?.previous != null) {
        queryClient.setQueryData(
          ['customization', { widgetId: variables.widgetId }],
          context.previous
        );
      }

      if (!variables.isStaleCleanup) {
        clearedStaleGroupOnlyColumnIdRef.current = null;
      }
    },
    onSettled: (
      _data,
      _error,
      { widgetId: mutationWidgetId, persistGeneration }
    ) => {
      if (persistGeneration !== groupOnlyPersistGenerationRef.current) {
        return;
      }

      queryClient.invalidateQueries({
        queryKey: ['customization', { widgetId: mutationWidgetId }],
      });
    },
  });

  const persistGroupOnlyCustomization = useCallback(
    (data: CustomizationDataDto, options?: { isStaleCleanup?: boolean }) => {
      mutateGroupOnlyColumnId({
        widgetId,
        data,
        persistGeneration: ++groupOnlyPersistGenerationRef.current,
        isStaleCleanup: options?.isStaleCleanup,
      });
    },
    [mutateGroupOnlyColumnId, widgetId]
  );

  const storedGroupOnlyColumnId = isCustomizable
    ? (customizationData?.groupOnlyColumnId ?? null)
    : localGroupOnlyColumnId;
  const groupOnlyColumnId = useMemo(() => {
    if (!enableGroupOnlyVisualization || storedGroupOnlyColumnId == null) {
      return null;
    }

    return rawCustomizedColumns.some(
      (column) =>
        column.id === storedGroupOnlyColumnId && column.grouping != null
    )
      ? storedGroupOnlyColumnId
      : null;
  }, [
    enableGroupOnlyVisualization,
    rawCustomizedColumns,
    storedGroupOnlyColumnId,
  ]);

  const handleGroupOnlyColumnIdChange = useCallback(
    (columnId: string | null) => {
      const storedColumnId = customizationData?.groupOnlyColumnId ?? null;
      if (!isCustomizable) {
        setLocalGroupOnlyColumnId(columnId);
        return;
      }

      if (columnId === storedColumnId) {
        return;
      }

      if (storedColumnId != null) {
        clearedStaleGroupOnlyColumnIdRef.current = storedColumnId;
      }

      const columns = convertToCustomizationData(
        rawCustomizedColumns,
        columnId ?? undefined
      ).columns;

      persistGroupOnlyCustomization({
        columns,
        groupOnlyColumnId: columnId,
      });
    },
    [
      customizationData?.groupOnlyColumnId,
      isCustomizable,
      persistGroupOnlyCustomization,
      rawCustomizedColumns,
    ]
  );

  useEffect(() => {
    if (
      !enableGroupOnlyVisualization ||
      !isCustomizable ||
      isGroupOnlyColumnIdPending
    ) {
      return;
    }

    const stored = customizationData?.groupOnlyColumnId ?? null;
    if (stored == null || stored === groupOnlyColumnId) {
      clearedStaleGroupOnlyColumnIdRef.current = null;
      return;
    }

    if (clearedStaleGroupOnlyColumnIdRef.current === stored) {
      return;
    }

    clearedStaleGroupOnlyColumnIdRef.current = stored;

    const columns = convertToCustomizationData(rawCustomizedColumns).columns;

    persistGroupOnlyCustomization(
      {
        columns,
        groupOnlyColumnId: null,
      },
      { isStaleCleanup: true }
    );
  }, [
    customizationData?.groupOnlyColumnId,
    enableGroupOnlyVisualization,
    groupOnlyColumnId,
    isCustomizable,
    isGroupOnlyColumnIdPending,
    persistGroupOnlyCustomization,
    rawCustomizedColumns,
  ]);

  const groupOnlyVisualizationData = useGroupOnlyVisualization({
    customizedColumns: rawCustomizedColumns,
    enableGroupOnlyVisualization,
    groupOnlyColumnId,
    onGroupOnlyColumnIdChange: handleGroupOnlyColumnIdChange,
  });

  const customizedColumns = useMemo(
    () =>
      groupOnlyColumnId != null
        ? patchColumnsForGroupOnlyMode(rawCustomizedColumns, groupOnlyColumnId)
        : rawCustomizedColumns,
    [rawCustomizedColumns, groupOnlyColumnId]
  );

  return {
    customizedColumns,
    groupOnlyVisualizationData,
  };
}
